import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
// import reportWebVitals from './reportWebVitals';
import { ApolloClient, InMemoryCache, ApolloProvider, from } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { getTokens } from "./common/manage_tokens";
import { expireSession, isSessionExpiredError } from "./common/session";
import { getRateLimitRejection } from "./common/rateLimit";
import { notifyRateLimited } from "./common/rateLimitNotice";
import { getDeviceId } from "./common/device";
import { createUploadLink } from "apollo-upload-client";
import { config } from "./config";
import { ThemeProvider, createTheme } from "@mui/material";
const authLink = setContext((_, { headers }) => {
  const tokenString = getTokens();

  return {
    headers: {
      ...headers,
      "Apollo-Require-Preflight": "true",
      "x-device-id": getDeviceId(),
      authorization: tokenString ? `Bearer ${tokenString}` : "",
    },
  };
});

// Catches an expired or rejected token for every operation in the app, rather
// than only on the screens that happen to render <ErrorNotifier />.
// expireSession() no-ops when signed out, so failed logins stay on the login
// screen, and only the first of several failed operations redirects.
const errorLink = onError((errorResponse) => {
  if (isSessionExpiredError(errorResponse)) expireSession();
});

/**
 * Surfaces a rate-limit rejection from any operation in the app, in either
 * shape the API produces: the per-operation limit that arrives as an HTTP 200
 * with a `TOO_MANY_REQUESTS` error, and the global per-IP envelope that arrives
 * as a bare HTTP 429 with no GraphQL body at all. The second one would
 * otherwise reach the screens as an unexplained network failure.
 *
 * Notifying only — nothing here retries. Retrying while blocked does not cost
 * the account anything (the server refunds a rejected request's windows), but
 * it turns one limit into a spinner that never resolves.
 */
const rateLimitLink = onError((errorResponse) => {
  const rejection = getRateLimitRejection(errorResponse);

  if (!rejection) return;

  notifyRateLimited(rejection);
});

const client = new ApolloClient({
  link: from([
    errorLink,
    rateLimitLink,
    authLink,
    createUploadLink({ uri: config.apiUrl }),
  ]),
  uri: config.apiUrl,
  cache: new InMemoryCache(),
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const theme = createTheme({
  components: {
    MuiTextField: {
      defaultProps: {
        variant: "standard",
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: "standard",
      },
    },
    MuiInputLabel: {
      defaultProps: {
        variant: "standard",
      },
    },
  },
});

root.render(
  <React.StrictMode>
    <ApolloProvider client={client}>
      <ThemeProvider theme={theme}>
        <App />
      </ThemeProvider>
    </ApolloProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
